import React, { useCallback, CSSProperties, HTMLProps } from 'react';
import { useTimelineHeadersContext } from './HeadersContext';
import { LEFT_VARIANT, RIGHT_VARIANT } from './constants';

interface GetRootProps {
  (props?: { style?: CSSProperties }): { style: CSSProperties };
}

interface SidebarHeaderProps {
  children: React.ComponentType<{ getRootProps: GetRootProps; data?: unknown }>;
  rightSidebarWidth?: number;
  leftSidebarWidth: number;
  variant: typeof LEFT_VARIANT | typeof RIGHT_VARIANT;
  headerData?: unknown;
}

function SidebarHeader({
  children: Renderer,
  rightSidebarWidth = 0,
  leftSidebarWidth,
  variant,
  headerData,
}: SidebarHeaderProps) {
  const getRootProps = useCallback<GetRootProps>(
    (props = {}) => {
      const { style = {} } = props;
      const width = variant === RIGHT_VARIANT ? rightSidebarWidth : leftSidebarWidth;
      return { style: { ...style, width } };
    },
    [variant, rightSidebarWidth, leftSidebarWidth],
  );

  return <Renderer getRootProps={getRootProps} data={headerData} />;
}

export type SidebarWrapperProps = {
  children?: React.ComponentType<{ getRootProps: GetRootProps }>;
  variant?: typeof LEFT_VARIANT | typeof RIGHT_VARIANT;
  headerData?: unknown;
};

const defaultChildren: NonNullable<SidebarWrapperProps['children']> = ({
  getRootProps,
}: {
  getRootProps: GetRootProps;
}) => <div data-testid="sidebarHeader" {...getRootProps()} />;

function SidebarWrapper(props: SidebarWrapperProps) {
  const { children, variant = LEFT_VARIANT, headerData } = props;
  const { leftSidebarWidth, rightSidebarWidth } = useTimelineHeadersContext();
  return (
    <SidebarHeader
      leftSidebarWidth={leftSidebarWidth}
      rightSidebarWidth={rightSidebarWidth}
      variant={variant}
      headerData={headerData}
    >
      {(children || defaultChildren) as SidebarHeaderProps['children']}
    </SidebarHeader>
  );
}

(SidebarWrapper as { secretKey?: string }).secretKey = 'SidebarHeader';

SidebarHeader.displayName = 'SidebarHeader';

export default SidebarWrapper;
